import React from 'react';
import { Field, useField } from 'react-final-form';
import InputField from 'components/InputField';
import { useTranslation } from 'react-i18next';
import TextAreaField from 'components/TextAreaField';
import validations from 'validations';
import ListingCreationSelectField from 'router/subrouters/ListingCreationWizard/components/ListingCreationSelectField';
import { APPOINTMENT_OPTIONS } from './utils';
import DeadlineArrayField from 'components/DeadlineArrayField';
import TextareaArrayField from 'components/TextareaArrayField';
import SelectField from 'components/SelectField';
import useOperationStepValues from 'router/data/useOperationStepValues';
import {
  DESCRIPTION_MAX_CHARACTERS,
  HIGHLIGHTS_MAX_CHARACTERS,
  KEY_ASSETS_MAX_CHARACTERS,
  PRODUCTS_AND_SERVICES_MAX_CHARACTERS,
  TITLE_MAX_CHARACTERS,
} from 'router/subrouters/ListingCreationWizard/utils';
import { MIN_REQUIRED_LIST_ITEMS } from 'router/subrouters/ListingCreationWizard/data/publicListingRequirements';
import ListingContactColleagues from 'router/subrouters/ListingCreationWizard/common-pages/ListingContactColleagues';
import ListingSlugField from 'router/subrouters/ListingCreationWizard/components/ListingSlugField';
import EnquiryRequirementsField from 'components/EnquiryRequirementsField';
import AiDraftBanner, {
  AI_DRAFT_BANNER_STEP,
} from 'router/subrouters/ListingCreationWizard/components/AiDraftBanner';

import './BusinessInfo.styles.scss';

type BusinessInfoProps = {
  className?: string;
};

const BusinessInfo: React.FC<BusinessInfoProps> = (props) => {
  const { t } = useTranslation();
  const {
    input: { value: appointmentType },
  } = useField('appointmentType');

  const { industrySelectOptions } = useOperationStepValues();

  return (
    <div className="lcw-page">
      <p className="lcw-page__heading__title">{t('businessListingInfo')}</p>

      <AiDraftBanner step={AI_DRAFT_BANNER_STEP.BUSINESS_INFO} />

      <Field
        label={t('appointmentType')}
        name="appointmentType"
        options={APPOINTMENT_OPTIONS}
        component={ListingCreationSelectField}
        validate={validations.required()}
      />
      {appointmentType === 'Other' && (
        <Field
          name="otherAppointmentType"
          component={InputField}
          placeholder={t('enterOtherAppointmentType')}
          validate={validations.required()}
        />
      )}
      <div className="lcw-custom-label-container">
        <h3>{t('keyListingInformation')}</h3>
      </div>
      <Field
        label={t('industryFieldLabel')}
        name="industry"
        options={industrySelectOptions}
        component={SelectField}
        validate={validations.required()}
        placeholder={t('selectAnIndustry')}
        required
        searchable
      />
      <Field
        label={t('listingTitle')}
        name="listingTitle"
        component={InputField}
        validate={validations.required()}
        placeholder={t('enterListingTitle')}
        required
        maxLength={TITLE_MAX_CHARACTERS}
      />
      <ListingSlugField />
      <Field
        label={t('postalCode')}
        placeholder={t('enterPostcode')}
        name="locationPostcode"
        type="number"
        required
        component={InputField}
        validate={validations.composeValidators(
          validations.required(),
          validations.auPostalCode(t('postalCodeInvalid')),
        )}
      />
      <Field
        label={t('shortDescription')}
        name="shortDescription"
        component={InputField}
        validate={validations.required()}
        placeholder={t('shortDescriptionPlaceholder')}
        maxLength={200}
        required
      />
      <Field
        className="lcw-description-field-textarea"
        label={t('fullDescription')}
        required
        name="businessDescription"
        component={TextAreaField}
        validate={validations.required()}
        placeholder={t('thisWillApearProminently')}
        maxLength={DESCRIPTION_MAX_CHARACTERS}
      />
      <div className="lcw-custom-label-container">
        <h3>{t('businessHighlights')}</h3>
      </div>
      <TextareaArrayField
        name="highlights"
        fieldLabel={t('highlight')}
        validate={validations.requireBuisinessInfoArrays()}
        maxFieldLength={HIGHLIGHTS_MAX_CHARACTERS}
        minRequired={MIN_REQUIRED_LIST_ITEMS}
        required
      />
      <div className="lcw-custom-label-container">
        <h3>{t('products/Services')}</h3>
      </div>
      <TextareaArrayField
        name="products"
        fieldLabel={t('products/Services')}
        validate={validations.requireBuisinessInfoArrays()}
        maxFieldLength={PRODUCTS_AND_SERVICES_MAX_CHARACTERS}
        minRequired={MIN_REQUIRED_LIST_ITEMS}
        required
      />
      <div className="lcw-custom-label-container">
        <h3>{t('keyAssets')}</h3>
      </div>
      <TextareaArrayField
        name="keyAssets"
        fieldLabel={t('keyAsset')}
        validate={validations.requireBuisinessInfoArrays()}
        maxFieldLength={KEY_ASSETS_MAX_CHARACTERS}
        required
        minRequired={MIN_REQUIRED_LIST_ITEMS}
      />

      <DeadlineArrayField />

      <EnquiryRequirementsField />

      <ListingContactColleagues />
    </div>
  );
};

export default BusinessInfo;
